import { FeatureFlags } from "@packages/feature-flags";
import {
  ProductListConfig,
  ResultsInfo,
  RenderableProduct,
} from "./types/types";
import { useEnrichedProducts } from "./utils/useEnrichedProducts";
import { layoutMap as listLayouts } from "./product-list-layouts/layoutMap";
import { raise } from "./utils/raise";
import { FeatureFlagsProvider } from "./flags/flags";
import { useVisibleProducts } from "./utils/useVisibleProducts";
import { Ref, useEffect, useRef } from "react";
import { createProductListEventDispatcher } from "./utils/events/dispatchEvent";
import {
  PRODUCT_LIST_EMPTY,
  PRODUCT_LIST_READY,
  PRODUCT_LIST_WARNING,
} from "./utils/events/productListEvents";

type ProductListProps = {
  products: RenderableProduct[];
  config: ProductListConfig;
  experiencesRawString?: string;
  resultsInfo: ResultsInfo;
  heading?: {
    main: string;
    subHeading: string | null;
  };
  flags: FeatureFlags;
  instanceId?: string;
  consumerId: string;
  warnings?: Record<string, unknown>[];
};

export function ProductList({
  products,
  config,
  experiencesRawString,
  resultsInfo,
  heading,
  flags,
  instanceId,
  consumerId,
  warnings,
}: ProductListProps) {
  const ref = useRef<HTMLElement | null>(null);
  const dispatch = createProductListEventDispatcher(ref);

  const visibleProducts = useVisibleProducts(products, flags, ref);
  const enrichedProducts = useEnrichedProducts(
    visibleProducts,
    config,
    consumerId,
    instanceId,
  );

  const { name: listLayoutName, props: listLayoutProps } =
    config["product-list-layout"];

  const ListLayout =
    listLayouts[listLayoutName] ||
    raise(`Couldn't find product-list layout "${listLayoutName}"`);

  useEffect(function dispatchProductListLifecycleEvents() {
    if (ref.current) {
      dispatch({
        type: PRODUCT_LIST_READY,
        instanceId: instanceId || "",
      });
      if (!products || visibleProducts.length === 0) {
        dispatch({
          type: PRODUCT_LIST_EMPTY,
          instanceId: instanceId || "",
        });
      }
      warnings?.forEach((warning) =>
        dispatch({
          type: PRODUCT_LIST_WARNING,
          ...warning,
          consumerId,
          instanceId: instanceId || "",
        }),
      );
    }
  }, []);

  useEffect(
    function logRender() {
      if (typeof window?.newrelic?.addPageAction === "function") {
        window.newrelic?.addPageAction("productCardListRendered", {
          config: config.name,
          instanceId,
          consumerId,
          productCount: products?.length,
        });
      }
    },
    [config.name, instanceId, consumerId, products?.length],
  );

  return (
    <FeatureFlagsProvider
      flags={flags}
      experiencesRawString={experiencesRawString}
      id="product-list"
    >
      <template
        ref={ref as Ref<HTMLTemplateElement>}
        data-id="product-list-event-emitter"
      />
      <ListLayout
        config={config}
        products={enrichedProducts}
        configProps={listLayoutProps}
        resultsInfo={resultsInfo}
        heading={heading}
        instanceId={instanceId || ""}
        consumerId={consumerId}
      />
    </FeatureFlagsProvider>
  );
}
